import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCoffeeRecords, CoffeeRecord } from '../utils/api';
import { useSampleOrderModal } from '../App';
import { usePageMeta } from '../utils/usePageMeta';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';

export const SamplePackPage: React.FC = () => {
  usePageMeta({
    title: 'Request Samples - Kenyan Specialty Coffee | Lot 20 Coffee',
    description: "Request the full current sample set of Lot 20's experimental Kenyan specialty coffees. Free shipping to the UK & EU; $60 USD DHL shipping worldwide.",
    canonicalPath: '/sample-pack'
  });

  const [coffees, setCoffees] = useState<CoffeeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { openModal } = useSampleOrderModal();

  useEffect(() => {
    fetchCoffeeRecords()
      .then(records => setCoffees(records))
      .catch(() => setCoffees([]))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      {/* Header */}
      <section className="pt-3 pb-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow className="mb-3">For Roasters</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-[#404040] mb-4">
            Request Samples
          </h1>
          <p className="font-display text-lg text-[#6B6B6B] max-w-2xl mb-8">
            One sample set — every coffee in our current lineup. Free shipping to the UK &amp; EU;
            $60 USD via DHL everywhere else, which covers the full set plus a phytosanitary certificate.
          </p>
          <Button onClick={() => openModal()} variant="primary" size="lg">
            Request Samples →
          </Button>
        </div>
      </section>

      {/* What's included */}
      <section className="pb-24 md:pb-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Eyebrow>What's Included</Eyebrow>
            <div className="flex-1 border-t border-[#E8E6E2]" />
            {!isLoading && <span className="text-xs text-[#A3A3A3]">{coffees.length} lots</span>}
          </div>

          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="border border-[#E8E6E2] animate-pulse">
                  <div className="aspect-[4/5] bg-[#F0EDE8]" />
                  <div className="p-4">
                    <div className="h-3 bg-[#F0EDE8] w-20 mb-2" />
                    <div className="h-4 bg-[#F0EDE8] w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : coffees.length === 0 ? (
            <p className="text-[#A3A3A3]">No coffees available right now. Check back soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {coffees.map(coffee => (
                <Link
                  key={coffee.id}
                  to={`/coffee/${coffee.slug}`}
                  className="flex flex-col border border-[#E8E6E2] hover:border-[#A3A3A3] transition-colors duration-200"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#F0EDE8]">
                    <img
                      src={coffee.imageUrl}
                      alt={coffee.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#A3A3A3] mb-1">
                      {coffee.processType}
                    </p>
                    <h3 className="text-sm font-display text-[#404040] leading-snug">
                      {coffee.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-16 text-center">
            <Button onClick={() => openModal()} variant="primary" size="lg">
              Request Samples →
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};
